import api from '@pray/shared/utils/api';

import type {
  ContentAnalyticsApiResponse,
  ContentAnalyticsData,
  ContentAnalyticsMetrics,
  ContentAnalyticsMetricsApiResponse,
} from './types';

interface GetContentAnalyticsParams {
  artistId: string;
  contentId: string;
  startDate: string;
  endDate: string;
  signal?: AbortSignal;
  timeout?: number;
}

function mapMetrics(metrics: ContentAnalyticsMetricsApiResponse | null | undefined): ContentAnalyticsMetrics | null {
  if (!metrics) return null;

  return {
    totalCount: metrics.total_count ?? 0,
    totalDuration: metrics.total_duration ?? 0,
    totalCompleted: metrics.total_completed ?? 0,
    uniqueListeners: metrics.unique_listeners ?? null,
    rank: metrics.rank ?? null,
    rankOutOf: metrics.rank_out_of ?? null,
    dailyData: metrics.daily_data ?? [],
  };
}

/**
 * Per-episode listen and watch analytics from
 * `GET /web/studio/artists/:artistId/content/:contentId/analytics`.
 */
export async function getContentAnalytics({
  artistId,
  contentId,
  startDate,
  endDate,
  signal,
  timeout,
}: GetContentAnalyticsParams): Promise<ContentAnalyticsData> {
  if (!artistId) throw new Error('Artist ID is required');
  if (!contentId) throw new Error('Content ID is required');

  const params = new URLSearchParams({ startDate, endDate });
  const url = `/web/studio/artists/${encodeURIComponent(artistId)}/content/${encodeURIComponent(
    contentId
  )}/analytics?${params.toString()}`;
  const response = await api.executeGetRequest<{ data: ContentAnalyticsApiResponse }>(url, {}, { signal, timeout });
  const { data } = response.data;

  return {
    contentId: data.content_id ?? contentId,
    dateRange: {
      startDate: data.date_range?.start_date ?? startDate,
      endDate: data.date_range?.end_date ?? endDate,
    },
    listen: mapMetrics(data.listen),
    watch: mapMetrics(data.watch),
  };
}
